import React, { useMemo, useState } from 'react';
import Box from '../Box/Box';
import filterConditionalKeyValues from '../../utils/filterConditionalKeyValues';
import Asset from '../../components/Asset';
import { ImageChildren } from './ImageChildrenType';

type TImageBase = {
  as: keyof React.JSX.IntrinsicElements;
  kind?: 'avatar' | 'background';
  alt?: string;
  title?: string;
  src?: string;
  loading?: 'lazy' | 'eager' | 'auto';
  fetchpriority?: 'high' | 'low' | 'auto';
  decoding?: 'async' | 'sync' | 'auto';
  style?: React.CSSProperties;
  className?: string;
  aspectRatio?: 'landscape' | 'portrait' | 'square';
  tabIndex?: number;
  children?: ImageChildren;
  forceImageContainer?: boolean;
};

const ImageBase = ({ as, kind, className = '', src, tabIndex, children, forceImageContainer, ...rest }: TImageBase) => {
  const [isLoading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const conditionalProps = useMemo(() => filterConditionalKeyValues({ modifiers: kind }), [kind]);

  const handleError = () => {
    setHasError(true);
    setLoading(false);
  };

  const handleLoad = () => {
    setLoading(false);
    setHasError(false);
  };

  const renderImageContainer = () => {
    return (
      <Box as="div" name="Image-container" className="hz-Image-container">
        <Box
          {...rest}
          {...conditionalProps}
          as={as}
          name="Image"
          className={className}
          src={src}
          onError={handleError}
          onLoad={handleLoad}
          tabIndex={tabIndex}
        />
        {children}
      </Box>
    );
  };

  const renderError = () => (
    <Box as="div" name="Image" className={`hz-Image--error ${className}`}>
      <Asset.Square name="design-system-no-image-placeholder" size="xxl" />
      {children}
    </Box>
  );

  const renderLoading = () => <Box as="div" name="Image" className={`hz-Image--loading ${className}`} />;

  const renderContent = () => {
    if (isLoading && !hasError) return renderLoading();
    if (hasError) return renderError();
    if (src || forceImageContainer) return renderImageContainer();
    return renderError();
  };

  return renderContent();
};

export default ImageBase;
